import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { publicRequest } from "../api/requestMethods";

const initialState = {
  query: "",
  results: {
    restaurants: [],
    retailers: [],
    healthcare: [],
    restaurantProducts: [],
    retailerProducts: [],
    healthcareProducts: [],
    brands: [],
    brandProducts: [],
    deals: [],
    wholesaleProducts: [],
    generalListings: [],
  },
  allResults: [],
  suggestions: [],
  totalCount: 0,
  isSearching: false,
  isFetchingSuggestions: false,
  error: null,
  hasSearched: false,
};

// Global search across all entities
export const globalSearch = createAsyncThunk(
  "globalSearch/search",
  async (searchQuery, { rejectWithValue }) => {
    try {
      if (!searchQuery || searchQuery.trim().length < 2) {
        return { results: initialState.results, allResults: [], totalCount: 0 };
      }

      const response = await publicRequest.get(`/search/global?q=${encodeURIComponent(searchQuery.trim())}&limit=20`);

      if (!response.data.success) {
        return rejectWithValue(response.data.message || "Search failed");
      }

      return {
        query: searchQuery,
        results: response.data.results,
        allResults: response.data.allResults,
        totalCount: response.data.totalCount,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Search failed. Please try again."
      );
    }
  }
);

// Quick search suggestions (autocomplete)
export const fetchSearchSuggestions = createAsyncThunk(
  "globalSearch/suggestions",
  async (searchQuery, { rejectWithValue }) => {
    try {
      if (!searchQuery || searchQuery.trim().length < 2) {
        return { suggestions: [] };
      }

      const response = await publicRequest.get(`/search/suggestions?q=${encodeURIComponent(searchQuery.trim())}`);

      return {
        suggestions: response.data.suggestions || [],
      };
    } catch (error) {
      return rejectWithValue([]);
    }
  }
);

const globalSearchSlice = createSlice({
  name: "globalSearch",
  initialState,
  reducers: {
    clearSearch: (state) => {
      state.query = "";
      state.results = initialState.results;
      state.allResults = [];
      state.suggestions = [];
      state.totalCount = 0;
      state.hasSearched = false;
      state.error = null;
    },
    clearSuggestions: (state) => {
      state.suggestions = [];
    },
    setSearchQuery: (state, action) => {
      state.query = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Global Search
      .addCase(globalSearch.pending, (state) => {
        state.isSearching = true;
        state.error = null;
      })
      .addCase(globalSearch.fulfilled, (state, action) => {
        state.isSearching = false;
        state.query = action.payload.query || "";
        state.results = action.payload.results;
        state.allResults = action.payload.allResults;
        state.totalCount = action.payload.totalCount;
        state.hasSearched = true;
        state.error = null;
      })
      .addCase(globalSearch.rejected, (state, action) => {
        state.isSearching = false;
        state.error = action.payload;
        state.hasSearched = true;
      })
      // Search Suggestions
      .addCase(fetchSearchSuggestions.pending, (state) => {
        state.isFetchingSuggestions = true;
      })
      .addCase(fetchSearchSuggestions.fulfilled, (state, action) => {
        state.isFetchingSuggestions = false;
        state.suggestions = action.payload.suggestions;
      })
      .addCase(fetchSearchSuggestions.rejected, (state) => {
        state.isFetchingSuggestions = false;
        state.suggestions = [];
      });
  },
});

export const { clearSearch, clearSuggestions, setSearchQuery } = globalSearchSlice.actions;

export default globalSearchSlice.reducer;
